import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Eye, Edit, MoreVertical, UserCheck, UserX, Trash2 } from 'lucide-react'
import type { UserListItem } from '@/modules/users/types/user'
import { DataTable } from '@/shared/components/ui/data-table'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

interface UsersTableProps {
  users?: UserListItem[]
  onView?: (id: number) => void
  onEdit?: (id: number) => void
  onToggleActive?: (id: number, currentStatus: boolean) => void
  onDelete?: (id: number) => void
}

const getInitials = (firstName: string, lastName: string) => {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return `${first}${last}` || 'U'
}

const getStatusBadge = (isActive: boolean) => {
  return isActive ? (
    <Badge className="badge-gradient hover:opacity-90">Activo</Badge>
  ) : (
    <Badge variant="outline" className="text-gray-400 border-gray-400">Inactivo</Badge>
  )
}

const getRoleBadge = (isStaff: boolean, isSuperuser: boolean) => {
  if (isSuperuser) {
    return <Badge className="badge-gradient hover:opacity-90">Superusuario</Badge>
  } else if (isStaff) {
    return <Badge className="badge-gradient hover:opacity-90">Staff</Badge>
  }
  return <Badge variant="outline" className="text-gray-400 border-gray-400">Usuario</Badge>
}

export function UsersTable({ users = [], onView, onEdit, onToggleActive, onDelete }: UsersTableProps) {
  const columns: ColumnDef<UserListItem>[] = [
    {
      accessorKey: 'usuario',
      header: 'Usuario',
      cell: ({ row }) => {
        const user = row.original
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-teal-500/10 text-teal-700 dark:text-teal-400 font-medium">
                {getInitials(user.first_name, user.last_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">
                {user.first_name} {user.last_name}
              </div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'informacion',
      header: 'Información',
      cell: ({ row }) => {
        const info = row.getValue('informacion') as string
        return info ? (
          <span className="text-sm">{info}</span>
        ) : (
          <span className="text-sm text-muted-foreground">Sin información</span>
        )
      },
    },
    {
      accessorKey: 'groups',
      header: 'Grupos',
      cell: ({ row }) => {
        const groups = row.getValue('groups') as string[]
        if (!groups || groups.length === 0) {
          return <span className="text-sm text-muted-foreground">Sin grupos</span>
        }
        return (
          <div className="flex flex-wrap gap-1">
            {groups.slice(0, 2).map((group) => (
              <Badge key={group} className="badge-gradient hover:opacity-90 text-xs">
                {group}
              </Badge>
            ))}
            {groups.length > 2 && (
              <Badge className="badge-gradient hover:opacity-90 text-xs">
                +{groups.length - 2}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'laboratorios',
      header: 'Laboratorios',
      cell: ({ row }) => {
        const labs = row.getValue('laboratorios') as string[]
        if (!labs || labs.length === 0) {
          return <span className="text-sm text-muted-foreground">—</span>
        }
        return (
          <div className="flex flex-wrap gap-1">
            {labs.slice(0, 2).map((lab) => (
              <Badge key={lab} variant="outline" className="text-xs">
                {lab}
              </Badge>
            ))}
            {labs.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{labs.length - 2}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      id: 'role',
      header: 'Rol',
      cell: ({ row }) => {
        const user = row.original
        return getRoleBadge(user.is_staff, user.is_superuser)
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Estado',
      cell: ({ row }) => {
        return getStatusBadge(row.getValue('is_active'))
      },
    },
    {
      accessorKey: 'last_login',
      header: 'Último acceso',
      cell: ({ row }) => {
        const lastLogin = row.getValue('last_login') as string | null
        if (!lastLogin) {
          return <span className="text-sm text-muted-foreground">Nunca</span>
        }
        try {
          const distance = formatDistanceToNow(new Date(lastLogin), {
            addSuffix: true,
            locale: es,
          })
          return <span className="text-sm">{distance}</span>
        } catch {
          return <span className="text-sm text-muted-foreground">-</span>
        }
      },
    },
    {
      id: 'acciones',
      header: () => <div className="text-right">Acciones</div>,
      cell: ({ row }) => {
        const user = row.original
        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {onView && (
                  <DropdownMenuItem onClick={() => onView(user.id)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver detalles
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(user.id)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onToggleActive && (
                  <DropdownMenuItem onClick={() => onToggleActive(user.id, user.is_active)}>
                    {user.is_active ? (
                      <>
                        <UserX className="mr-2 h-4 w-4" />
                        Desactivar
                      </>
                    ) : (
                      <>
                        <UserCheck className="mr-2 h-4 w-4" />
                        Activar
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(user.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  return <DataTable columns={columns} data={users} />
}
